import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { SelectionType } from 'guideline/domain';
import { of, switchMap, take } from 'rxjs';
import { ComponentConverter } from 'shared/domain';

import {
  ActivityDefinitionDto,
  ComponentDto,
  ComponentType,
  FlowchartElementType,
  FlowchartLinkDto,
  FlowchartStartNodeDto,
  GuidelineComponentType,
  ProposedComponentDto,
} from '@treetop/shared/model';

import { BasicNode } from './BasicNode';
import { IFlowchartNode } from './IFlowchartNode';
import { FlowchartSidebarStoreService } from '../../../../../guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import { FlowchartGenericDto } from '../infrastructure/base/flowchart-events.service';
import OperationsService from '../infrastructure/operations/operations.service';
import SubflowService from '../infrastructure/operations/subflow.service';

export class FlowchartStartNode extends BasicNode<FlowchartStartNodeDto> implements IFlowchartNode {
  constructor(element: FlowchartStartNodeDto) {
    super(element);
  }

  addOutgoingLink(link: FlowchartLinkDto): void {
    this.element.outgoingLinks = this.element.outgoingLinks || [];
    this.element.outgoingLinks.push(link);
  }

  removeOutgoingLink(linkIndex: number): void {
    this.element.outgoingLinks.splice(linkIndex, 1);
  }

  addToGraph(
    graph: joint.dia.Graph,
    paper: joint.dia.Paper,
    operationsService: OperationsService,
    subflowService: SubflowService,
    translocoService?: TranslocoService,
    allowEvents = false,
  ): void {
    let title = '';
    if (this.element.guidelineAction) {
      title = title.concat(this.element.guidelineAction.title);
    }

    if (this.element.guidelineCriteria) {
      if (title) {
        title = title.concat('|');
      }
      title = title.concat(this.element.guidelineCriteria.title);
    }

    const prettifiedRule = this.element.rulePrettyfied
      ? new DOMParser().parseFromString(this.element.rulePrettyfied || this.element.rule, 'text/html').documentElement.textContent
      : this.element.rule;
    const titleStartNode = title ? title : prettifiedRule || 'no title';
    const size = {
      width: this.element.width,
      height: this.element.height,
    };

    const startElement = operationsService.createStart(titleStartNode, {
      tooltip: titleStartNode,
      id: this.element.id,
      uniqueId: this.element.uniqueId,
      x: this.element.positionX,
      y: this.element.positionY,
      size,
      colorPresetId: this.element.colorPresetId,
    });

    graph.addCell(startElement);

    const description = this.element?.guidelineAction?.description || this.element?.guidelineCriteria?.description;

    operationsService.setClickableForSmartbook(this.element.id, description);
    operationsService.addElementTags(this.element);

    if (this.element?.showDescription) {
      operationsService.initElementsDescription(this.element.id, description);
    }
  }

  setPropertiesForInit(element: joint.dia.Cell): void {
    super.setPropertiesForInit(element);
    this.element.elementType = FlowchartElementType.FlowchartStartNode;
  }

  async setPropertiesAfterSearch(
    definition: ComponentDto | ProposedComponentDto,
    medicationRequests: ActivityDefinitionDto[],
    componentConverter: ComponentConverter,
    selectedElementView: joint.dia.ElementView,
    translocoService?: TranslocoService,
    flowchartSidebarStoreService?: FlowchartSidebarStoreService,
  ): Promise<void> {
    const copiedObject: FlowchartGenericDto = JSON.parse(JSON.stringify(this.element));

    if (selectedElementView) {
      copiedObject.width = selectedElementView.model.attributes.size.width;
      copiedObject.height = selectedElementView.model.attributes.size.height;
    }

    of(definition)
      .pipe(
        switchMap((definition) => {
          if (definition.code.startsWith('PC-')) {
            return of(componentConverter.convertProposedComponentToAction(definition));
          } else if ((definition as ComponentDto).type === ComponentType.FindingDefinition) {
            return of(componentConverter.convertFindingToCriterion(definition));
          } else if ((definition as ComponentDto).type === ComponentType.ActivityDefinition) {
            return componentConverter.convertActivityToAction(definition, medicationRequests, copiedObject.uniqueId);
          } else {
            return of(definition);
          }
        }),
        take(1),
      )
      .subscribe((convertedDefinition) => {
        if ('componentType' in convertedDefinition && convertedDefinition.componentType === GuidelineComponentType.Criteria) {
          copiedObject.guidelineCriteria = convertedDefinition;
          copiedObject.guidelineCriteriaId = copiedObject.guidelineCriteria.id;
        } else {
          copiedObject.guidelineAction = convertedDefinition;
          copiedObject.guidelineActionId = copiedObject.guidelineAction.id;
        }

        this.element = copiedObject;

        flowchartSidebarStoreService?.setDto({
          element: copiedObject,
          type: SelectionType.Flowchart,
        });
      });
  }
}
